// Copyright (c) Docugami, Inc. All rights reserved.

import automationAnywhereLogo from 'assets/AutomationAnywhereLogo.png';
import { ActionTileProps } from "components/homepage/ActionTiles";
import { automationAnywhereColor, powerAutomateColor, primaryTheme, zapierColor } from "components/shell/presentation/AppStyles";
import { ActionTileId } from "hooks/ActionTileItems";
import { dgIconNames } from "hooks/dgIcons";
import { INavigationFunctions, NavigationType } from "hooks/navigationService";
import { useAppSelector } from "hooks/redux";
import { DocSetCollectionType, WorkPathType } from "networking/WorkPathApi";
import { CreateWorkPathSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import createAA from "../../assets/CreateAA.png";
import createAbstract from "../../assets/CreateAbstract.png";
import createExcel from "../../assets/CreateExcel.png";
import createReport from "../../assets/CreateReport.png";
import { IconDescriptor } from "./IconRenderer";

export enum WorkPathCategory {
    Understand = "Understand",
    Create = "Create",
    Workflow = "Workflow"
}

const PreviewUnderstandingWpTypes: WorkPathType[] = [
    WorkPathType.DiligenceReport
];

const UnderstandingWpTypes: WorkPathType[] = [
    WorkPathType.TabularReport,
    WorkPathType.ExcelExport,
    ...PreviewUnderstandingWpTypes
];

const PreviewCreatingWpTypes: WorkPathType[] = [
    WorkPathType.AssistedAuthoring,
    WorkPathType.AutomationAnywhereDocumentAssembly,
    WorkPathType.UiPathDocumentAssembly
];

const CreatingWpTypes: WorkPathType[] = [
    WorkPathType.Abstract,
    ...PreviewCreatingWpTypes
];

const PreviewWorkFlowWpTypes: WorkPathType[] = [
    WorkPathType.AutomationAnywhereWorkFlow,
    WorkPathType.UiPathWorkFlow,
    WorkPathType.PowerAutomateWorkFlow
];

const WorkFlowWpTypes: WorkPathType[] = [
    WorkPathType.ZapierWorkFlow,
    ...PreviewWorkFlowWpTypes
];

// Workpaths hidden under the demo feature flag
const WorkpathFeatureFlagTypes: WorkPathType[] = [
    WorkPathType.PowerAutomateWorkFlow
];

const PreviewWorkpathTypes: WorkPathType[] = [
    ...PreviewUnderstandingWpTypes,
    ...PreviewCreatingWpTypes,
    ...PreviewWorkFlowWpTypes,
    ...WorkpathFeatureFlagTypes
];

export const reportWorkPathTypes = [
    WorkPathType.Abstract,
    WorkPathType.AutomationAnywhereDocumentAssembly,
    WorkPathType.AutomationAnywhereWorkFlow,
    WorkPathType.ExcelExport,
    WorkPathType.PowerAutomateWorkFlow,
    WorkPathType.TabularReport,
    WorkPathType.UiPathDocumentAssembly,
    WorkPathType.UiPathWorkFlow,
    WorkPathType.ZapierWorkFlow,
    WorkPathType.DiligenceReport
];

export const isReportRequired = (type: WorkPathType): boolean => {
    return reportWorkPathTypes.includes(type);
}

export function getFilterTypesForWorkPathCategory(category?: WorkPathCategory, workflowFeatureEnabled?: boolean) {
    const excludedTypes = !workflowFeatureEnabled ? WorkpathFeatureFlagTypes : [];

    let types = [];
    switch (category) {
        case WorkPathCategory.Understand:
            types = [...UnderstandingWpTypes];
            break;
        case WorkPathCategory.Create:
            types = [...CreatingWpTypes];
            break;
        case WorkPathCategory.Workflow:
            types = [...WorkFlowWpTypes];
            break;
        default:
            types = Object.keys(WorkPathType) as WorkPathType[];
    }

    return types.filter(t => !excludedTypes.includes(t));
}

/**
 * Returns the Icon name, height multiplier, and width multiplier for your dimensions.
 * @param wpType The type of workpath you want the icon for
 */
export function getIconInfoForWorkPath(wpType: WorkPathType, isLogo: boolean = false, isTitle: boolean = false): IconDescriptor {
    switch (wpType) {
        case WorkPathType.TabularReport:
            return isTitle ?
                {
                    name: createReport,
                    height: 1,
                    width: 0.85,
                    isImage: true
                } :
                {
                    name: dgIconNames.DgReportsFileIcon,
                    height: 1,
                    width: 1.39,
                }

        case WorkPathType.ExcelExport:
            return isTitle ?
                {
                    name: createExcel,
                    height: 1,
                    width: .97,
                    isImage: true
                } :
                {
                    name: dgIconNames.DgExcelExportFileIcon,
                    height: 1,
                    width: .84
                }

        case WorkPathType.Abstract:
            return isTitle ?
                {
                    name: createAbstract,
                    height: 1,
                    width: 0.77,
                    isImage: true
                } :
                {
                    name: dgIconNames.DgAbstractFileIcon,
                    height: 1,
                    width: 0.84,
                }

        case WorkPathType.AssistedAuthoring:
            return isTitle ?
                {
                    name: createAA,
                    height: 1,
                    width: 1.01,
                    isImage: true
                } :
                {
                    name: dgIconNames.DgAssistedAuthoringFileIcon,
                    height: 1,
                    width: 0.84,
                }

        case WorkPathType.AutomationAnywhereDocumentAssembly:
            return isLogo || isTitle ? {
                name: automationAnywhereLogo,
                height: .37,
                width: 1,
                isImage: true
            } : {
                name: dgIconNames.DgAutomationAnywhereFileIcon,
                height: 1,
                width: .84
            }

        case WorkPathType.AutomationAnywhereWorkFlow:
            return isLogo || isTitle ? {
                name: automationAnywhereLogo,
                height: .37,
                width: 1,
                isImage: true
            } : {
                name: dgIconNames.DgAutomationAnywhereIcon,
                height: 1,
                width: 1,
                defaultColor: automationAnywhereColor
            }

        case WorkPathType.ZapierWorkFlow:
            return isLogo ? {
                name: dgIconNames.DgZapierLogo,
                height: .56,
                width: 1,
                defaultColor: zapierColor
            } : {
                name: dgIconNames.DgZapierIcon,
                height: 1,
                width: 1,
                defaultColor: zapierColor
            };

        case WorkPathType.UiPathWorkFlow:
            return isLogo || isTitle ? {
                name: dgIconNames.DgUiPathLogo,
                height: .356,
                width: 1
            } : {
                name: dgIconNames.DgUiPathWorkflowIcon,
                height: 1,
                width: 1
            };

        case WorkPathType.UiPathDocumentAssembly:
            return isLogo || isTitle ? {
                name: dgIconNames.DgUiPathLogo,
                height: .356,
                width: 1
            } : {
                name: dgIconNames.DgUiPathDocumentAssemblyFileIcon,
                height: 1,
                width: .84
            };

        case WorkPathType.PowerAutomateWorkFlow:
            return isLogo || isTitle ? {
                name: dgIconNames.DgPowerAutomateLogo,
                height: .44,
                width: 1
            } : {
                name: dgIconNames.DgPowerAutomateIcon,
                height: 1,
                width: 1,
                defaultColor: powerAutomateColor
            };

        case WorkPathType.DiligenceReport:
            return isTitle ?
                {
                    name: dgIconNames.DgDiligenceFileIcon,
                    height: 0.8,
                    width: 1.1,
                    defaultColor: primaryTheme.palette.themePrimary
                } :
                {
                    name: dgIconNames.DgDiligenceFileIcon,
                    height: 1,
                    width: 1.38,
                    defaultColor: primaryTheme.palette.themePrimary
                }

        default:
            throw new Error(`Improper Work Path Type: ${WorkPathType[wpType]}`);
    }
}

/**
 * Returns the Icon name, height multiplier, and width multiplier for your dimensions.
 * @param wpCategory The category of workpath you want the icon for
 */
export function getIconInfoForWorkPathCategory(wpCategory: WorkPathCategory): IconDescriptor {
    switch (wpCategory) {
        case WorkPathCategory.Understand:
            return getIconInfoForWorkPath(WorkPathType.TabularReport);

        case WorkPathCategory.Create:
            return {
                name: dgIconNames.DgAuthoredDocsIcon,
                height: 1,
                width: 0.84,
            }

        case WorkPathCategory.Workflow:
            return {
                name: dgIconNames.DgWorkflowIcon,
                height: 1,
                width: 1.39
            }

        default:
            throw new Error(`Improper Work Path Category: ${WorkPathCategory[wpCategory]}`);
    }
}

interface WorkPathCategoryInfo {
    title: string;
    itemsTitle: string;
    subheading: string;
}

export const workPathCategoryToDisplayInfo: { [id: string]: WorkPathCategoryInfo } = {
    [WorkPathCategory.Understand]: { title: 'REPORTS', itemsTitle: "My Reports", subheading: 'Extract data from your documents to generate reports and gain insights' },
    [WorkPathCategory.Create]: { title: 'AUTHORING', itemsTitle: "Authored Docs", subheading: 'Generate new docs using data from your doc sets' },
    [WorkPathCategory.Workflow]: { title: 'AUTOMATED WORKFLOWS', itemsTitle: "My Workflows", subheading: 'Generate automated workflows utilizing a number of different tools you are already familiar with' },
};

interface WorkPathActionsInfo {
    id: ActionTileId;
    label: string;
    useLogo: boolean;
}

export const typeToInfoMap = new Map<WorkPathType, WorkPathActionsInfo>([
    [WorkPathType.TabularReport, { id: ActionTileId.TabularReport, label: "Generate Tabular Report", useLogo: false }],
    [WorkPathType.ExcelExport, { id: ActionTileId.ExcelExport, label: "Extract Data to Excel", useLogo: false }],
    [WorkPathType.Abstract, { id: ActionTileId.Abstract, label: "Generate Abstract", useLogo: false }],
    [WorkPathType.AssistedAuthoring, { id: ActionTileId.AssistedAuthoring, label: "Author a New Document", useLogo: false }],
    [WorkPathType.AutomationAnywhereDocumentAssembly, { id: ActionTileId.AutomationAnywhereDocumentAssembly, label: "Automation Anywhere Document Assembly", useLogo: false }],
    [WorkPathType.AutomationAnywhereWorkFlow, { id: ActionTileId.AutomationAnywhereWorkFlow, label: "Automation Anywhere Workflow", useLogo: true }],
    [WorkPathType.ZapierWorkFlow, { id: ActionTileId.ZapierWorkFlow, label: "Zapier Workflow", useLogo: false }],
    [WorkPathType.UiPathDocumentAssembly, { id: ActionTileId.UiPathDocumentAssembly, label: "UiPath Document Assembly", useLogo: false }],
    [WorkPathType.UiPathWorkFlow, { id: ActionTileId.UiPathWorkFlow, label: "UiPath Workflow", useLogo: true }],
    [WorkPathType.PowerAutomateWorkFlow, { id: ActionTileId.PowerAutomateWorkFlow, label: "Power Automate Workflow", useLogo: true }],
    [WorkPathType.DiligenceReport, { id: ActionTileId.DiligenceReport, label: "Generate Diligence Report", useLogo: false }]
]);

export const typeToLabel = new Map<WorkPathType, string>([
    [WorkPathType.TabularReport, "Tabular Report"],
    [WorkPathType.ExcelExport, "Excel Export"],
    [WorkPathType.Abstract, "Abstract"],
    [WorkPathType.AssistedAuthoring, "Assisted Authoring"],
    [WorkPathType.AutomationAnywhereDocumentAssembly, "Automation Anywhere Document Assembly"],
    [WorkPathType.AutomationAnywhereWorkFlow, "Automation Anywhere Workflow"],
    [WorkPathType.ZapierWorkFlow, "Zapier Workflow"],
    [WorkPathType.UiPathDocumentAssembly, "UiPath Document Assembly"],
    [WorkPathType.UiPathWorkFlow, "UiPath Workflow"],
    [WorkPathType.PowerAutomateWorkFlow, "Power Automate Workflow"],
    [WorkPathType.DiligenceReport, "Diligence Report"]
]);

export function createWorkPathActions(types: WorkPathType[], navigation: INavigationFunctions, workflowFeatureEnabled: boolean): { [id: string]: ActionTileProps } {
    function navToWorkPathPage(type: WorkPathType) {
        Telemetry.trackEvent<CreateWorkPathSchema>({
            workPathType: type,
            eventName: TelemetryEvent.CreateWorkPath
        });
        navigation.navToNewWorkPathPage(NavigationType.PushNewPage, type, undefined, DocSetCollectionType.All);
    }

    // Filter types
    let targetTypes = [...types];
    if (!workflowFeatureEnabled) {
        targetTypes = targetTypes.filter(type => !WorkpathFeatureFlagTypes.includes(type));
    }

    const workPathActionsProps: { [id: string]: ActionTileProps } = {};
    targetTypes.filter(x => typeToInfoMap.has(x)).forEach(type => {
        const info = typeToInfoMap.get(type);
        const iconInfo = getIconInfoForWorkPath(type, info?.useLogo, true);
        const id = info!.id;

        workPathActionsProps[id] = {
            id: id,
            actionText: info!.label,
            iconInfo: iconInfo,
            onClick: () => navToWorkPathPage(type),
            isPreview: PreviewWorkpathTypes.includes(type)
        }
    });

    return workPathActionsProps;
}

export function useDiligenceFlaggingEnabled() {
    const currentWorkPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType);
    return currentWorkPathType === WorkPathType.DiligenceReport;
}

export const isAbstractViewEnabled = (workPathType?: WorkPathType) => workPathType === WorkPathType.Abstract;
